import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import { t } from 'ember-intl';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/tas/state-pill';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { DataSourceInfo, LiveTasData } from 'tio-employee/services/reimagined-data';

interface TasProgramShowModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
  programId: string;
}

interface TasProgramShowSignature {
  Args: {
    model: TasProgramShowModel;
  };
}

export default class CareerPathwaysTasProgramShow extends Component<TasProgramShowSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get program() {
    return this.args.model.tasData.programs.find(
      (program) => program.id === this.args.model.programId
    );
  }

  <template>
    <VStack class="gap-6">
      <div class="tio-col-tight">
        <Heading @level={{1}}>{{t "common.programs"}}</Heading>
        <Text @variant="muted">{{t "common.view"}}</Text>
      </div>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-program-show"
      >
        <Section>
          <:header>
            {{t "common.view"}}
          </:header>
          <:body>
            {{#if this.program}}
              <div class="tio-row-start-between gap-4">
                <div>
                  <Heading @level={{3}}>{{this.program.name}}</Heading>
                  {{#if this.program.institution}}
                    <Text @variant="muted">{{this.program.institution}}</Text>
                  {{/if}}
                  {{#if this.program.major}}
                    <Text @variant="muted">
                      {{t
                        "tuition_assistance.reimagined.dashboard.label_major"
                        major=this.program.major
                      }}
                    </Text>
                  {{/if}}
                  {{#if this.program.startDate}}
                    <Text @variant="muted">
                      {{t
                        "tuition_assistance.reimagined.programs.label_starts"
                        date=this.program.startDate
                      }}
                    </Text>
                  {{/if}}
                </div>
                <StatePill @state={{this.program.state}} />
              </div>
              <div class="mt-4">
                <Button
                  @route="authenticated.tas.programs.show"
                  @model={{@model.programId}}
                  @intent="primary"
                >
                  {{t "common.view"}}
                </Button>
              </div>
            {{else}}
              <Text @variant="muted">{{t
                  "tuition_assistance.reimagined.programs.empty_body"
                }}</Text>
            {{/if}}
          </:body>
        </Section>
      </DataIndicator>
    </VStack>
  </template>
}
